import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loginPortalForRole, ROLES } from '../config/rbac';

function LoadingScreen() {
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="spinner-border text-primary" role="status" aria-label="Loading" />
    </div>
  );
}

export function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;

  if (!isAuthenticated) {
    const isBrandPath = location.pathname.startsWith('/brand');
    const loginPath = isBrandPath ? '/brand/login' : '/login';
    return <Navigate to={loginPath} replace state={{ from: location }} />;
  }

  return children;
}

export function GuestRoute({ children, portal = 'super' }) {
  const { isAuthenticated, loading, role } = useAuth();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  if (loading) return <LoadingScreen />;

  if (isAuthenticated) {
    const userPortal = loginPortalForRole(role);
    if (portal === 'brand' && userPortal !== 'brand') {
      return <Navigate to="/login" replace />;
    }
    if (portal === 'super' && userPortal === 'brand') {
      return <Navigate to="/brand/login" replace />;
    }
    return <Navigate to={from} replace />;
  }

  return children;
}

/**
 * Restrict route to specific roles and/or permissions.
 */
export function RoleRoute({ children, roles, permission, permissions, fallback = '/' }) {
  const { loading, hasRole, hasPermission, hasAnyPermission } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;

  if (roles && !hasRole(roles)) {
    return <Navigate to={fallback} replace state={{ from: location, denied: true }} />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to={fallback} replace state={{ from: location, denied: true }} />;
  }

  if (permissions?.length && !hasAnyPermission(permissions)) {
    return <Navigate to={fallback} replace state={{ from: location, denied: true }} />;
  }

  return children;
}

export function SuperAdminRoute({ children, ...rest }) {
  return (
    <RoleRoute roles={[ROLES.SUPER_ADMIN]} {...rest}>
      {children}
    </RoleRoute>
  );
}

export function BrandAdminRoute({ children, ...rest }) {
  return (
    <RoleRoute roles={[ROLES.BRAND_ADMIN]} {...rest}>
      {children}
    </RoleRoute>
  );
}
